import React, {
  createContext,
  Fragment,
  ReactNode,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';
import LoadingScreen from '../components/LoadingScreen';

export type VgsFieldContextState = {
  // all fields that should be loaded
  allFields: Set<string>;
  // all fields that have been loaded
  loadedFields: Set<string>;
};

export type VgsFieldAction =
  | {
      type: 'ADD_LOADED_FIELD';
      value: string;
    }
  | { type: 'ADD_FIELD'; value: string }
  | { type: 'REMOVE_FIELD'; value: string };

const VgsFieldContext = createContext<{
  state: VgsFieldContextState;
  dispatch: React.Dispatch<VgsFieldAction>;
}>({
  state: { allFields: new Set<string>(), loadedFields: new Set<string>() },
  dispatch: () => false,
});

export default VgsFieldContext;

function vgsFieldReducer(
  state: VgsFieldContextState,
  action: VgsFieldAction,
): VgsFieldContextState {
  switch (action.type) {
    case 'ADD_FIELD': {
      state.loadedFields.delete(action.value);
      state.allFields.add(action.value);
      return {
        ...state,
        loadedFields: new Set(state.loadedFields),
        allFields: new Set(state.allFields),
      };
    }
    case 'ADD_LOADED_FIELD': {
      state.loadedFields.add(action.value);
      return { ...state, loadedFields: new Set(state.loadedFields) };
    }
    case 'REMOVE_FIELD': {
      state.loadedFields.delete(action.value);
      state.allFields.delete(action.value);
      return {
        ...state,
        loadedFields: new Set(state.loadedFields),
        allFields: new Set(state.allFields),
      };
    }
    default:
      return state;
  }
}

export function VgsFieldProvider({
  children,
  visible,
  loadingElement,
}: {
  children: ReactNode | ReactNode[];
  visible?: boolean;
  loadingElement?: ReactNode;
}) {
  const [state, dispatch] = useReducer(vgsFieldReducer, {
    allFields: new Set<string>(),
    loadedFields: new Set<string>(),
  });

  const [shouldBeVisible, setShouldBeVisible] = useState(false);

  useEffect(() => {
    if (
      state.allFields.size > 0 &&
      (visible === true || visible === undefined)
    ) {
      setShouldBeVisible(state.allFields.size === state.loadedFields.size);
    }
  }, [state, state.allFields.size, state.loadedFields.size, visible]);
  useEffect(() => {
    if (visible === false) {
      setShouldBeVisible(false);
    }
  }, [visible]);

  return (
    <VgsFieldContext.Provider
      value={{
        state,
        dispatch,
      }}
    >
      <div
        style={{
          visibility: shouldBeVisible ? 'visible' : 'hidden',
          height: shouldBeVisible ? 'auto' : 0,
          position: shouldBeVisible ? 'static' : 'absolute',
          top: shouldBeVisible ? 'auto' : 0,
        }}
      >
        {children}
      </div>
      {visible && !shouldBeVisible && (
        <Fragment>
          {loadingElement ? loadingElement : <LoadingScreen />}
        </Fragment>
      )}
    </VgsFieldContext.Provider>
  );
}

export const useVgsFieldContext = () => {
  const context = useContext(VgsFieldContext);
  if (!context) {
    throw new Error('VgsFieldContext must be used within a VgsCollectProvider');
  }
  return context;
};
